import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import { FaChevronDown, FaSearch, FaTimes, FaRegCalendarAlt } from 'react-icons/fa';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

// Filters for the Recent Calls table. All filtering is client-side over the
// already-fetched rows, so these controls only manage selection state and hand
// it back to the parent via the on*Change callbacks. The parent owns the
// filter state and the derived filtered list (see twilio_agent/index.jsx).

const MEMBER_STATUS_SEGMENTS = [
  { value: 'all', label: 'All' },
  { value: 'member', label: 'Member' },
  { value: 'trial', label: 'Trial' },
  { value: 'none', label: 'No plan' }
];

const DIRECTION_SEGMENTS = [
  { value: 'all', label: 'All' },
  { value: 'incoming', label: 'IN' },
  { value: 'outgoing', label: 'OUT' }
];

// Segmented pill toggle styled after the reference image (nav-blue track,
// raised white knob on the active segment). Single-select; one segment is always
// active (the 'all' segment is the "no filter" state). Used for both the
// member-status and call-direction filters.
function SegmentedToggle({ label, segments, value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
        {label}
      </span>
      <div
        style={{
          display: 'inline-flex',
          padding: 3,
          borderRadius: 9999,
          background: 'var(--surface-nav)',
          gap: 2
        }}
      >
        {segments.map((seg) => {
          const active = value === seg.value;
          return (
            <button
              key={seg.value}
              type="button"
              onClick={() => onChange(seg.value)}
              style={{
                padding: '4px 14px',
                borderRadius: 9999,
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                border: 'none',
                background: active ? 'var(--surface-card)' : 'transparent',
                color: active ? 'var(--text-primary)' : 'var(--text-on-nav)',
                boxShadow: active ? '0 1px 2px rgba(0,0,0,0.25)' : 'none',
                transition: 'background 0.15s ease'
              }}
            >
              {seg.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Multi-select dropdown with type-to-filter search. Reused for both the ops
// agent and call-status filters. Closes on outside click. An empty `selected`
// array means "all".
function MultiSelectDropdown({ label, options, selected, onChange, placeholder, width = 200 }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  let summary = placeholder;
  if (selected.length === 1) {
    summary = options.find((o) => o.value === selected[0])?.label || '1 selected';
  } else if (selected.length > 1) {
    summary = `${selected.length} selected`;
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          minWidth: width,
          padding: '6px 10px',
          borderRadius: 8,
          fontSize: 13,
          cursor: 'pointer',
          border: '1px solid',
          borderColor: selected.length ? 'var(--brand-primary)' : 'var(--input-border)',
          background: 'var(--input-bg)',
          color: selected.length ? 'var(--text-primary)' : 'var(--input-placeholder)'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{label}: </span>
          {summary}
        </span>
        <FaChevronDown size={11} style={{ flexShrink: 0, color: 'var(--text-muted)' }} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 30,
            top: 'calc(100% + 4px)',
            left: 0,
            minWidth: Math.max(width, 220),
            maxWidth: 320,
            background: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-modal)',
            padding: 8
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              borderRadius: 6,
              border: '1px solid var(--input-border)',
              background: 'var(--input-bg)',
              marginBottom: 6
            }}
          >
            <FaSearch size={11} style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter…"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13,
                color: 'var(--input-text)'
              }}
            />
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                title="Clear selection"
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: 0,
                  display: 'inline-flex'
                }}
              >
                <FaTimes size={12} />
              </button>
            )}
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text-muted)' }}>
                No matches
              </div>
            ) : (
              filtered.map((opt) => {
                const checked = selected.includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '2px 8px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 13,
                      lineHeight: '20px',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(opt.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {opt.label}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Sidebar quick-ranges shown in the picker's left column. Last week / Last
// Sidebar quick-ranges shown in the picker's left column.
const buildStaticRanges = () =>
  createStaticRanges([
    {
      label: 'Yesterday',
      range: () => ({
        startDate: moment().subtract(1, 'day').startOf('day').toDate(),
        endDate: moment().subtract(1, 'day').endOf('day').toDate()
      })
    },
    {
      label: 'Last 7 days',
      range: () => ({
        startDate: moment().subtract(6, 'days').startOf('day').toDate(),
        endDate: moment().endOf('day').toDate()
      })
    },
    {
      label: 'Last 30 days',
      range: () => ({
        startDate: moment().subtract(29, 'days').startOf('day').toDate(),
        endDate: moment().endOf('day').toDate()
      })
    }
  ]);

// Trigger button + portal popover wrapping react-date-range's DateRangePicker.
// Replaces the old native From/To <input type="date"> pair in the Recent Calls
// header. Emits YYYY-MM-DD strings via onChange({ from, to }) — the same shape
// the parent's fetchRecentCalls already consumes.
export function DateRangeFilter({ from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  // Anchor the portal popover to the trigger and keep it positioned on
  // scroll / resize. Rendered into <body> so an ancestor's overflow can't
  // clip it.
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, left: rect.left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const handleClick = (e) => {
      const inRoot = rootRef.current && rootRef.current.contains(e.target);
      const inPop = popoverRef.current && popoverRef.current.contains(e.target);
      if (!inRoot && !inPop) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const range = [
    {
      startDate: from ? new Date(from) : new Date(),
      endDate: to ? new Date(to) : new Date(),
      key: 'selection'
    }
  ];

  const handleSelect = (item) => {
    const { startDate, endDate } = item.selection;
    onChange({
      from: moment(startDate).format('YYYY-MM-DD'),
      to: moment(endDate).format('YYYY-MM-DD')
    });
  };

  const label =
    from && to ? `${moment(from).format('MMM D')} → ${moment(to).format('MMM D')}` : 'Any date';

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          borderRadius: 8,
          fontSize: 13,
          cursor: 'pointer',
          border: '1px solid var(--input-border)',
          background: 'var(--input-bg)',
          color: 'var(--text-primary)'
        }}
      >
        <FaRegCalendarAlt size={12} style={{ color: 'var(--text-muted)' }} />
        <span>{label}</span>
        <FaChevronDown size={11} style={{ color: 'var(--text-muted)' }} />
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              className="twilio-rdr-scope"
              style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 10000 }}
            >
              {/* react-date-range leaves the static-range label color to
                  inherit, which renders white-on-white in this popover until
                  hover. Pin the label + surface colors to theme tokens so the
                  presets are readable, and theme the hover/selected states. */}
              <style>{`
                .twilio-rdr-scope .rdrStaticRanges { background: var(--surface-card); }
                .twilio-rdr-scope .rdrStaticRange {
                  background: var(--surface-card);
                  border-bottom-color: var(--border-default);
                }
                .twilio-rdr-scope .rdrStaticRangeLabel { color: var(--text-primary); }
                .twilio-rdr-scope .rdrStaticRange:hover .rdrStaticRangeLabel,
                .twilio-rdr-scope .rdrStaticRange:focus .rdrStaticRangeLabel {
                  background: var(--surface-sunken);
                }
              `}</style>
              <div
                style={{
                  overflow: 'hidden',
                  borderRadius: 8,
                  border: '1px solid var(--border-default)',
                  background: 'var(--surface-card)',
                  boxShadow: 'var(--shadow-modal)'
                }}
              >
                <DateRangePicker
                  ranges={range}
                  onChange={handleSelect}
                  moveRangeOnFirstSelection={false}
                  editableDateInputs
                  rangeColors={['#ee731b']}
                  showDateDisplay={false}
                  months={2}
                  direction="horizontal"
                  maxDate={new Date()}
                  inputRanges={[]}
                  staticRanges={buildStaticRanges()}
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

function RecentCallsFilters({
  memberStatus,
  onMemberStatusChange,
  dateFrom,
  dateTo,
  onDateChange,
  agentOptions,
  selectedAgents,
  onAgentsChange,
  statusOptions,
  selectedStatuses,
  onStatusesChange,
  direction,
  onDirectionChange,
  activeFilterCount,
  onClearAll
}) {
  return (
    <div
      className="px-5 py-3 border-b border-gray-200 flex items-center gap-4 flex-wrap"
      style={{ background: 'var(--surface-sunken)' }}
    >
      {/* <span className="text-xs font-medium text-gray-600">Date</span> */}
      <DateRangeFilter from={dateFrom} to={dateTo} onChange={onDateChange} />
      <SegmentedToggle
        label="Users"
        segments={MEMBER_STATUS_SEGMENTS}
        value={memberStatus}
        onChange={onMemberStatusChange}
      />
      <MultiSelectDropdown
        label="Agent"
        options={agentOptions}
        selected={selectedAgents}
        onChange={onAgentsChange}
        placeholder="All agents"
        width={170}
      />
      <MultiSelectDropdown
        label="Call status"
        options={statusOptions}
        selected={selectedStatuses}
        onChange={onStatusesChange}
        placeholder="All statuses"
        width={170}
      />
      <SegmentedToggle
        label="Call"
        segments={DIRECTION_SEGMENTS}
        value={direction}
        onChange={onDirectionChange}
      />
      {activeFilterCount > 0 && (
        <button
          type="button"
          onClick={onClearAll}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
            border: '1px solid var(--border-default)',
            background: 'var(--surface-card)',
            color: 'var(--text-secondary)'
          }}
        >
          <FaTimes size={11} />
          Clear filters ({activeFilterCount})
        </button>
      )}
    </div>
  );
}

export default RecentCallsFilters;
